import * as React from 'react';
import { Button, type ButtonProps } from '@repo/ui/src/components/ui/button';
import { Spinner } from '@repo/ui/src/components/ui/spinner';
import { cn } from '@repo/ui/src/lib/utils';

export interface BusyButtonProps extends ButtonProps {
  /** True while the action this button triggers is in flight. */
  busy: boolean;
  /** Shown next to the spinner while `busy` (e.g. "Đang lưu…"). */
  busyLabel: React.ReactNode;
}

/**
 * A Button that swaps to a spinner + busyLabel while `busy`, without ever
 * changing width. Both the idle `children` and the busy spinner+label render
 * into the same grid cell at all times — the inactive one `invisible` — so
 * the button's intrinsic width is fixed from first paint (the wider of the
 * two) and never reflows when `busy` flips.
 */
export const BusyButton = React.forwardRef<HTMLButtonElement, BusyButtonProps>(
  ({ busy, busyLabel, children, disabled, className, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        disabled={busy || disabled}
        aria-busy={busy}
        className={cn('grid grid-cols-1 grid-rows-1 items-center justify-items-center', className)}
        {...props}
      >
        <span className={cn('col-start-1 row-start-1 inline-flex items-center gap-2', busy && 'invisible')}>
          {children}
        </span>
        <span className={cn('col-start-1 row-start-1 inline-flex items-center gap-2', !busy && 'invisible')}>
          <Spinner />
          {busyLabel}
        </span>
      </Button>
    );
  },
);
BusyButton.displayName = 'BusyButton';
